import { useLocation, useNavigate } from "react-router-dom";
import { UserContext } from "../context/UserContext";
import { useContext, useEffect, useMemo, useState, useRef } from "react";
import { base_url } from '../url_variables';
import axios from 'axios';
import { useCookies } from "react-cookie";
import { BarLoader, ClockLoader, GridLoader } from "react-spinners";
import { MenuContext } from "../context/MenuContext";
import { TbPigMoney } from "react-icons/tb";
import { BiMoneyWithdraw } from "react-icons/bi";
import { TopBar } from "../components/topbar";
import { IoIosSearch } from "react-icons/io";
import { FaPlaneArrival, FaPlaneDeparture, FaPlaneSlash } from "react-icons/fa";
import { MdAirplanemodeActive } from "react-icons/md";
import { TostMessageContext } from "../context/TostMessage";
import { BellIcon, PinIcon, CloudOffIcon, AirportField, Icon, ToggleButton, FleetCard } from "../components/icons";
import { FaPeopleGroup } from "react-icons/fa6";
import { CharterNote } from "../components/add_charter_note";

export default function CharterSearch() {
    const [cookies] = useCookies();
    const [loading, setLoading] = useState(false);
    const [buttonLoading, setButtonLoading] = useState(false);
    const navigate = useNavigate();
    const {setTostMessage, userProfile} = useContext(TostMessageContext);
    const [pageData, setPageData] = useState({});
    const [airports, setAirports] = useState(cookies?.userProfile?.airports)
    const [jets, setJets] = useState([]);
    const [showModal, setShowModal] = useState(false);

    const newLeg = () => ({
        departure_airport: null,
        arrival_airport: null,
        departure_time: "",
        isReturn: false,
    });

    const [passengers, setPassengers] = useState(2);
    const [legs, setLegs] = useState([newLeg()]);
    const [returnEnabled, setReturnEnabled] = useState(false);
    const [view, setView] = useState("grid"); // "grid" | "list"
    const baseLegs = useMemo(() => legs.filter((l) => !l.isReturn), [legs]);
    const lastBaseLeg = baseLegs[baseLegs.length - 1] || null;
    const [showViewing, setShowViewing] = useState({});
    const sectionRef = useRef(null);

    const scrollToSection = () => {
        sectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
        });
    };

    // Keep "return leg" synced when toggle is enabled
    useEffect(() => {
        setLegs((prev) => {
        const withoutReturn = prev.filter((l) => !l.isReturn);

        if (!returnEnabled) return withoutReturn;

        const firstDep = withoutReturn?.[0]?.departure_airport || null;
        const lastArr = withoutReturn?.[withoutReturn.length - 1]?.arrival_airport || null;

        const returnLeg = {
            departure_airport: lastArr,
            arrival_airport: firstDep,
            departure_time: "", // user can set this
            isReturn: true,
        };

        return [...withoutReturn, returnLeg];
        });
    }, [returnEnabled]);

    // Also update return leg whenever base route changes (first dep / last arr)
    useEffect(() => {
        if (!returnEnabled) return;

        setLegs((prev) => {
        const withoutReturn = prev.filter((l) => !l.isReturn);
        const firstDep = withoutReturn?.[0]?.departure_airport || null;
        const lastArr = withoutReturn?.[withoutReturn.length - 1]?.arrival_airport || null;

        const existingReturn = prev.find((l) => l.isReturn);
        const nextReturn = {
            ...(existingReturn || newLeg()),
            isReturn: true,
            departure_airport: lastArr,
            arrival_airport: firstDep,
        };

        return [...withoutReturn, nextReturn];
        });
    }, [
        returnEnabled,
        // only track the values that matter
        baseLegs?.[0]?.departure_airport?.iata,
        lastBaseLeg?.arrival_airport?.iata,
    ]);

    const updateLeg = (idx, patch) => {
        setLegs((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
    };

    const addLeg = () => {
        setLegs((prev) => {
        const withoutReturn = prev.filter((l) => !l.isReturn);
        const next = [...withoutReturn, newLeg()];

        if (!returnEnabled) return next;

        // if return enabled, keep return at the end
        const firstDep = next?.[0]?.departure_airport || null;
        const lastArr = next?.[next.length - 1]?.arrival_airport || null;

        return [
            ...next,
            { departure_airport: lastArr, arrival_airport: firstDep, departure_time: "", isReturn: true },
        ];
        });
    };

    const removeLeg = (idx) => {
        setLegs((prev) => {
        const withoutReturn = prev.filter((l) => !l.isReturn);

        // Don’t allow removing if it would leave you with 1 base leg (optional)
        if (withoutReturn.length <= 1) return prev;

        const next = withoutReturn.filter((_, i) => i !== idx);

        if (!returnEnabled) return next;

        const firstDep = next?.[0]?.departure_airport || null;
        const lastArr = next?.[next.length - 1]?.arrival_airport || null;

        return [
            ...next,
            { departure_airport: lastArr, arrival_airport: firstDep, departure_time: "", isReturn: true },
        ];
        });
    };

    const canSearch = useMemo(() => {
        // Must have all legs filled (including return leg if enabled)
        return legs.every(
        (l) => l.departure_airport && l.arrival_airport && String(l.departure_time || "").length > 0
        );
    }, [legs]);

    const buildPayload = () => ({
        passengers,
        legs: legs.map((l) => ({
        departure_airport: l.departure_airport, // keep full airport object (or convert to iata if you prefer)
        arrival_airport: l.arrival_airport,
        departure_time: l.departure_time,
        })),
    });

    const submit = async () => {
        const payload = buildPayload();
        console.log("SEARCH PAYLOAD:", payload);

        const formData = new FormData();
        formData.append('passengers', payload?.passengers);
        formData.append('legs', JSON.stringify(payload?.legs));

        setButtonLoading(true)
        try {
            const response = await axios.post(base_url + '/charter-jet/flight-search.php', formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                    token: cookies?.userProfile?.session,
                },
            });
            if (response.data.status) {
                setJets(response.data.data);
                scrollToSection()
            } else {
                setTostMessage({
                    messageType: 'error',
                    message: response.data.message
                })
            }
            setButtonLoading(false)
        } catch (error) {
            setButtonLoading(false)
            setTostMessage({
                messageType: 'error',
                message: 'Network error'
            })
        }

    };

    const submitRequest = async (jet, note) => {
        const payload = buildPayload();

        const formData = new FormData();
        formData.append('jet', jet?.aircraft_id);
        formData.append('note', note);
        formData.append('passengers', payload?.passengers);
        formData.append('legs', JSON.stringify(payload?.legs));

        setButtonLoading(true)
        try {
            const response = await axios.post(base_url + '/charter-jet/request-quote.php', formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                    token: cookies?.userProfile?.session,
                },
            });
            if (response.data.status) {
                setTostMessage({
                    messageType: 'success',
                    message: response.data.message
                })
                setShowModal(false)
                setShowViewing({});
            } else {
                setTostMessage({
                    messageType: 'error',
                    message: response.data.message
                })
            }
            setButtonLoading(false)
        } catch (error) {
            setButtonLoading(false)
            setTostMessage({
                messageType: 'error',
                message: 'Network error'
            })
        }
    }

    const onRequest = async (jet) => {
        setShowModal(true);
        setShowViewing(jet);
        return;
    };

    const now = new Date();
    const minDateTime =
  now.getFullYear() +
  "-" +
  String(now.getMonth() + 1).padStart(2, "0") +
  "-" +
  String(now.getDate()).padStart(2, "0") +
  "T" +
  String(now.getHours()).padStart(2, "0") +
  ":" +
  String(now.getMinutes()).padStart(2, "0");

    return(
        <div className="w-full h-full">
            <TopBar />
            {loading && (
                <div className="flex items-center justify-center h-full w-[-webkit-fill-available] z-[99] fixed">
                    <ClockLoader
                        color={'#d8b05a'}
                        loading={true}
                        size={40}
                        aria-label="Loading Spinner"
                        data-testid="loader"
                    />
                </div>
            )}
            {showModal && 
                <CharterNote
                    onRequest={onRequest}
                    setShowModal={setShowModal}
                    showModal={showModal}
                    jet={showViewing}
                    submitRequest={submitRequest}
                />
            }
            <div className="w-full p-4 sm:p-6">
                <div>
                    <div className="flex items-center gap-3">
                        <div className="text-[#d8b05a]">
                            <FaPlaneDeparture size={20} />
                        </div>
                        <div className="text-xl font-semibold text-white/90">
                            Charter a Jet
                        </div>
                    </div>
                </div>
                <div className="relative overflow-hidden rounded-3xl border border-[#40392B] bg-[#2E281F]/40 p-5 sm:p-7 mt-3">
                    <div className="relative">
                    {/* Return toggle */}
                    <div className="mt-0 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div className="text-[12px] font-semibold tracking-[0.28em] text-[#d8b05a]/80">
                            CREATE YOUR JOURNEY
                        </div>
                    </div>

                    {/* Legs */}
                    <div className="mt-3 space-y-3">
                        {legs.map((leg, idx) => {
                        const prevTime = idx > 0 ? legs[idx - 1]?.departure_time : "";
                        const isReturn = !!leg.isReturn;
                        const label = isReturn
                            ? "RETURN"
                            : `LEG ${String(baseLegs.findIndex((b) => b === leg) + 1).padStart(2, "0")}`;

                        return (
                            <>
                            <div
                            key={idx}
                            className="rounded-2xl border border-[#40392B] bg-[#2E281F]/20 p-4 sm:p-5"
                            >
                            <div className="mb-3 flex items-center justify-between">
                                <span className="rounded-md bg-[#d8b05a]/20 px-3 py-1 text-[11px] font-semibold tracking-widest text-[#d8b05a]">
                                {label}
                                </span>

                                {!isReturn && baseLegs.length > 1 && (
                                <button
                                    type="button"
                                    onClick={() => removeLeg(idx)}
                                    className="rounded-lg p-2 text-white/40 hover:text-white/70"
                                    aria-label="Remove leg"
                                >
                                    {/* trash */}
                                    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none">
                                    <path
                                        d="M9 3h6m-8 4h10m-9 0 1 14h6l1-14"
                                        stroke="currentColor"
                                        strokeWidth="1.8"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                    <path
                                        d="M10 11v7M14 11v7"
                                        stroke="currentColor"
                                        strokeWidth="1.8"
                                        strokeLinecap="round"
                                    />
                                    </svg>
                                </button>
                                )}
                            </div>

                            <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1.2fr_1.2fr_1fr] lg:items-end">
                                <AirportField
                                label="FROM"
                                icon="plane-depart"
                                airports={airports}
                                value={leg.departure_airport}
                                onChange={(a) => updateLeg(idx, { departure_airport: a })}
                                placeholder="Search city, airport, or IATA"
                                Icon={Icon}
                                />

                                <AirportField
                                label="TO"
                                icon="plane-arrive"
                                airports={airports}
                                value={leg.arrival_airport}
                                onChange={(a) => updateLeg(idx, { arrival_airport: a })}
                                placeholder="Destination Airport (e.g. ABV)"
                                Icon={Icon}
                                />

                                <div className="min-w-0">
                                <div className="mb-2 text-[11px] tracking-[0.22em] text-white/50">
                                    DEPARTURE DATE &amp; TIME
                                </div>
                                <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-[#2E281F] px-4 py-3">
                                    {Icon ? <Icon name="calendar" className="text-white/50" /> : null}
                                    <input
                                    type="datetime-local"
                                    min={prevTime || minDateTime}
                                    value={leg.departure_time}
                                    onChange={(e) => updateLeg(idx, { departure_time: e.target.value })}
                                    className="w-full min-w-0 bg-transparent text-sm text-white/70 outline-none"
                                    />
                                </div>
                                </div>
                            </div>
                            </div>
                            {idx == legs?.length-1 &&
                                <button
                                type="button"
                                onClick={addLeg}
                                className="inline-flex items-center gap-3 text-sm font-semibold text-[#d8b05a]/90 hover:underline"
                                >
                                <span className="grid h-7 w-7 place-items-center rounded-full border border-[#d8b05a]/25 bg-[#d8b05a]/10">
                                    +
                                </span>
                                ADD ANOTHER STOP
                                </button>
                            }
                            </>
                        );
                        })}
                    </div>

                    {/* Footer actions */}
                    <div className="mt-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                        <button
                        type="button"
                        onClick={() => setReturnEnabled((v) => !v)}
                        className={[
                            "inline-flex items-center gap-3 rounded-full px-4 py-2",
                            "border border-[#d8b05a]/30 bg-white/10 text-sm font-semibold",
                            returnEnabled ? "text-[#d8b05a]" : "text-white/60",
                            "hover:bg-white/10 transition",
                        ].join(" ")}
                        >
                        <span
                            className={[
                            "h-5 w-9 rounded-full p-[3px] transition",
                            returnEnabled ? "bg-[#d8b05a]/40" : "bg-white/10",
                            ].join(" ")}
                        >
                            <span
                            className={[
                                "block h-3.5 w-3.5 rounded-full transition",
                                returnEnabled ? "translate-x-4 bg-[#d8b05a]" : "translate-x-0 bg-white/40",
                            ].join(" ")}
                            />
                        </span>
                        Add Return Leg
                        </button>
                        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between lg:justify-end lg:gap-6">
                        {/* Passenger stepper */}
                        <div className="flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/5 px-4 py-2">
                            <div className="text-sm text-white/55 flex items-center gap-2">
                            <FaPeopleGroup size={20} color="#d8b05a"/>
                            <span className="font-semibold text-[#d8b05a]">
                                {String(passengers).padStart(2, "0")}
                            </span>
                            <span className="text-[11px] tracking-[0.22em] text-white/40">
                                PASSENGERS
                            </span>
                            </div>

                            <div className="flex items-center gap-2">
                            <button
                                type="button"
                                onClick={() => setPassengers((p) => Math.max(1, p - 1))}
                                className="h-8 w-8 rounded-lg border border-white/10 bg-black/20 text-white/70 hover:bg-white/5"
                            >
                                −
                            </button>
                            <button
                                type="button"
                                onClick={() => setPassengers((p) => Math.min(20, p + 1))}
                                className="h-8 w-8 rounded-lg border border-white/10 bg-black/20 text-white/70 hover:bg-white/5"
                            >
                                +
                            </button>
                            </div>
                        </div>

                        {/* Search */}
                        <button
                            type="button"
                            disabled={!canSearch}
                            onClick={submit}
                            className={[
                            "h-12 w-full sm:w-auto rounded-xl px-6 text-sm font-semibold tracking-widest transition",
                            canSearch
                                ? "bg-[#d8b05a] text-black hover:brightness-110 active:brightness-95"
                                : "bg-[#d8b05a]/50 text-white/40 cursor-not-allowed",
                            "focus:outline-none focus:ring-4 focus:ring-[#d8b05a]/20",
                            ].join(" ")}
                        >   
                            {buttonLoading ? <BarLoader 
                                color={'#ffffff'}
                                loading={true}
                                size={7}
                                className="mx-auto"
                                aria-label="Loading Spinner"
                                data-testid="loader"
                            /> : 
                            <>
                                SEARCH AVAILABLE JETS
                            </>
                            }
                        </button>
                        </div>
                    </div>
                    </div>
                </div>
                <div ref={sectionRef} className="mx-auto w-full max-w-7xl mt-6">
                    {/* Header */}
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                        <h1 className="text-xl sm:text-xl font-semibold text-white/90">
                        Available Fleet for your Journey
                        </h1>
                        <p className="mt-1 text-sm text-white/50">
                        Estimated prices include fuel surcharges and landing fees.
                        </p>
                    </div>

                    <div className="flex items-center gap-2 self-start">
                        <ToggleButton
                        active={view === "grid"}
                        onClick={() => setView("grid")}
                        icon="grid"
                        label="Grid view"
                        />
                        <ToggleButton
                        active={view === "list"}
                        onClick={() => setView("list")}
                        icon="list"
                        label="List view"
                        />
                    </div>
                    </div>

                    <div className="mt-5 h-px w-full bg-white/10" />

                    {/* Results */}
                    <div
                    className={[
                        "mt-6 grid gap-6",
                        view === "grid"
                        ? "grid-cols-1 md:grid-cols-2 xl:grid-cols-3"
                        : "grid-cols-1",
                    ].join(" ")}
                    >
                    {jets?.map((jet) => (
                        <FleetCard key={jet.id} jet={jet} view={view} onRequest={onRequest} />
                    ))}
                    </div>
                </div>
            </div>
        </div>
    )
}